import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { getSessionHeaders } from "@/lib/session";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Plus, Pencil, Trash2, Loader2, Plug, PlugZap, Server, ChevronDown, Wrench } from "lucide-react";

interface DataSource {
  id: string;
  name: string;
  description: string | null;
  url: string;
  auth_headers: Record<string, string>;
  enabled: boolean;
  internal: boolean;
  created_at: string;
  updated_at: string;
}

interface FormData {
  name: string;
  description: string;
  url: string;
  auth_headers: string;
  enabled: boolean;
}

const emptyForm: FormData = {
  name: "",
  description: "",
  url: "",
  auth_headers: "{}",
  enabled: true,
};

interface McpTool {
  name: string;
  description: string;
}

const DataSources = () => {
  const { user, profile, markHasData } = useAuth();
  const { toast } = useToast();
  const [sources, setSources] = useState<DataSource[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState<FormData>(emptyForm);
  const [toolsMap, setToolsMap] = useState<Record<string, McpTool[]>>({});
  const [loadingTools, setLoadingTools] = useState<Record<string, boolean>>({});

  const fetchSources = async () => {
    const { data, error } = await supabase.from("data_sources").select("*").order("created_at", { ascending: false });
    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } else {
      setSources((data as unknown as DataSource[]) || []);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchSources();
  }, []);

  const openCreate = () => {
    setEditId(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEdit = (source: DataSource) => {
    setEditId(source.id);
    setForm({
      name: source.name,
      description: source.description || "",
      url: source.url,
      auth_headers: JSON.stringify(source.auth_headers || {}, null, 2),
      enabled: source.enabled,
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!user) return;
    let parsedHeaders: Record<string, string> = {};
    try {
      parsedHeaders = JSON.parse(form.auth_headers);
    } catch {
      toast({ title: "Invalid JSON", description: "Auth headers must be valid JSON.", variant: "destructive" });
      return;
    }

    if (!form.name.trim() || !form.url.trim()) {
      toast({ title: "Required", description: "Name and URL are required.", variant: "destructive" });
      return;
    }

    setSaving(true);
    const payload = {
      name: form.name.trim(),
      description: form.description.trim() || null,
      url: form.url.trim(),
      auth_headers: parsedHeaders,
      enabled: form.enabled,
      user_id: user.id,
      client_id: profile?.client_id ?? "",
    };

    let error;
    if (editId) {
      // Prevent updating internal data sources
      const target = sources.find((s) => s.id === editId);
      if (target?.internal) {
        toast({ title: "Not allowed", description: "Internal data sources cannot be edited.", variant: "destructive" });
        setSaving(false);
        return;
      }
      const { user_id, ...updatePayload } = payload;
      ({ error } = await supabase
        .from("data_sources")
        .update(updatePayload as any)
        .eq("id", editId));
    } else {
      ({ error } = await supabase.from("data_sources").insert(payload as any));
    }

    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } else {
      toast({ title: editId ? "Updated" : "Created", description: `Data source "${form.name}" saved.` });
      markHasData();
      setDialogOpen(false);
      fetchSources();
    }
    setSaving(false);
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    // Prevent deleting internal data sources
    const target = sources.find((s) => s.id === deleteId);
    if (target?.internal) {
      toast({ title: "Not allowed", description: "Internal data sources cannot be deleted.", variant: "destructive" });
      setDeleteId(null);
      return;
    }
    const { error } = await supabase.from("data_sources").delete().eq("id", deleteId);
    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "Deleted", description: "Data source removed." });
      fetchSources();
    }
    setDeleteId(null);
  };

  const handleToggle = async (id: string, enabled: boolean) => {
    // Prevent toggling internal data sources
    const target = sources.find((s) => s.id === id);
    if (target?.internal) return;
    const { error } = await supabase
      .from("data_sources")
      .update({ enabled } as any)
      .eq("id", id);
    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } else {
      setSources((prev) => prev.map((s) => (s.id === id ? { ...s, enabled } : s)));
    }
  };

  const handleTest = async (source: DataSource) => {
    setTesting(source.id);
    try {
      const { data, error } = await supabase.functions.invoke("mcp-proxy", {
        headers: await getSessionHeaders(),
        body: { data_source_id: source.id },
      });
      if (error) throw error;
      const toolCount = data?.tool_count ?? 0;
      toast({ title: "Connection successful", description: `Found ${toolCount} tool(s) on this MCP server.` });
    } catch (e: any) {
      toast({
        title: "Connection failed",
        description: e.message || "Could not reach the MCP server.",
        variant: "destructive",
      });
    }
    setTesting(null);
  };

  const fetchTools = async (source: DataSource) => {
    if (toolsMap[source.id]?.length) return; // already loaded successfully
    setLoadingTools((prev) => ({ ...prev, [source.id]: true }));
    try {
      const { data, error } = await supabase.functions.invoke("mcp-proxy", {
        headers: await getSessionHeaders(),
        body: { data_source_id: source.id },
      });
      if (error) {
        const msg = error instanceof Error ? error.message : "Unknown error";
        throw new Error(msg);
      }
      if (data?.error) {
        throw new Error(data.error);
      }
      const tools: McpTool[] = (data?.tools || []).map((t: any) => ({
        name: t.name,
        description: t.description || "",
      }));
      setToolsMap((prev) => ({ ...prev, [source.id]: tools }));
    } catch (e: any) {
      // Don't cache failure — allow retry on next click
      toast({
        title: "Could not fetch tools",
        description: e?.message || `Failed to reach ${source.name}`,
        variant: "destructive",
      });
    }
    setLoadingTools((prev) => ({ ...prev, [source.id]: false }));
  };

  if (loading) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="flex flex-1 flex-col gap-6 p-6 overflow-y-auto">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">MCP Servers</h1>
          <p className="text-sm text-muted-foreground">
            Connect MCP servers to extend AI capabilities with any AI model or tool.
          </p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={openCreate}>
              <Plus className="h-4 w-4" />
              Add MCP Server
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>{editId ? "Edit" : "Add"} MCP Server</DialogTitle>
              <DialogDescription>Configure a Model Context Protocol server endpoint.</DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label htmlFor="name">Name *</Label>
                <Input
                  id="name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g. My Data Server"
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="description">Description</Label>
                <Input
                  id="description"
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="Optional description"
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="url">Server URL *</Label>
                <Input
                  id="url"
                  value={form.url}
                  onChange={(e) => setForm({ ...form, url: e.target.value })}
                  placeholder="https://example.com/mcp-server"
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="auth_headers">Auth Headers (JSON)</Label>
                <Textarea
                  id="auth_headers"
                  value={form.auth_headers}
                  onChange={(e) => setForm({ ...form, auth_headers: e.target.value })}
                  placeholder='{"Authorization": "Bearer ..."}'
                  className="font-mono text-xs"
                  rows={3}
                />
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={form.enabled} onCheckedChange={(enabled) => setForm({ ...form, enabled })} />
                <Label>Enabled</Label>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={saving}>
                {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                {editId ? "Save Changes" : "Add Server"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {sources.length === 0 ? (
        <Card className="flex flex-col items-center justify-center py-12">
          <Server className="h-12 w-12 text-muted-foreground mb-4" />
          <CardTitle className="text-lg mb-1">No data sources configured</CardTitle>
          <CardDescription>Add an MCP server to give your AI assistant access to external tools.</CardDescription>
        </Card>
      ) : (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>URL</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sources.map((source) => {
                const tools = toolsMap[source.id];
                const isLoadingTools = loadingTools[source.id];
                const canEdit = !source.internal;
                return (
                  <Collapsible key={source.id} asChild>
                    <>
                      <TableRow>
                        <TableCell>
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-medium">{source.name}</span>
                              {source.internal && (
                                <Badge variant="outline" className="text-xs">
                                  Internal
                                </Badge>
                              )}
                            </div>
                            {source.description && (
                              <div className="text-xs text-muted-foreground">{source.description}</div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="max-w-[200px] truncate font-mono text-xs">{source.url}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Switch
                              checked={source.enabled}
                              onCheckedChange={(enabled) => handleToggle(source.id, enabled)}
                              disabled={source.internal}
                            />
                            <Badge variant={source.enabled ? "default" : "secondary"}>
                              {source.enabled ? "Active" : "Disabled"}
                            </Badge>
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <CollapsibleTrigger asChild>
                              <Button variant="ghost" size="icon" onClick={() => fetchTools(source)} title="View tools">
                                {isLoadingTools ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <Wrench className="h-4 w-4" />
                                )}
                              </Button>
                            </CollapsibleTrigger>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleTest(source)}
                              disabled={testing === source.id}
                              title="Test connection"
                            >
                              {testing === source.id ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <PlugZap className="h-4 w-4" />
                              )}
                            </Button>
                            {canEdit && (
                              <Button variant="ghost" size="icon" onClick={() => openEdit(source)} title="Edit">
                                <Pencil className="h-4 w-4" />
                              </Button>
                            )}
                            {canEdit && (
                              <Button variant="ghost" size="icon" onClick={() => setDeleteId(source.id)} title="Delete">
                                <Trash2 className="h-4 w-4 text-destructive" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                      <CollapsibleContent asChild>
                        <tr>
                          <td colSpan={4} className="border-b px-4 pb-4 pt-0">
                            {isLoadingTools ? (
                              <div className="flex items-center gap-2 py-2 text-sm text-muted-foreground">
                                <Loader2 className="h-3 w-3 animate-spin" /> Loading tools…
                              </div>
                            ) : tools && tools.length > 0 ? (
                              <div className="grid gap-1 py-2">
                                <div className="text-xs font-medium text-muted-foreground mb-1">
                                  {tools.length} tool(s) available
                                </div>
                                {tools.map((tool) => (
                                  <div
                                    key={tool.name}
                                    className="flex items-start gap-2 rounded-md border border-border bg-muted/30 px-3 py-2"
                                  >
                                    <Wrench className="mt-0.5 h-3 w-3 shrink-0 text-muted-foreground" />
                                    <div>
                                      <div className="text-sm font-medium font-mono">{tool.name}</div>
                                      {tool.description && (
                                        <div className="text-xs text-muted-foreground">{tool.description}</div>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ) : tools ? (
                              <div className="py-2 text-sm text-muted-foreground">No tools found on this server.</div>
                            ) : null}
                          </td>
                        </tr>
                      </CollapsibleContent>
                    </>
                  </Collapsible>
                );
              })}
            </TableBody>
          </Table>
        </Card>
      )}

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete data source?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove this MCP server configuration. The AI will no longer have access to its
              tools.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default DataSources;
